import {
  Button,
  DataTable,
  Dropdown,
  Icon,
  Menu,
  MenuItem,
  PageSection,
  Spacer,
  Stack,
  TextCaption,
  TextTitle,
  Tooltip,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';
import { ColumnDef } from '@tanstack/react-table';
import { nothing } from 'lit';
import { html } from 'lit/static-html.js';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useDeleteChannelModal } from '../components';
import {
  StudioPageProps,
  useChannelAnalyticsToggle,
  useProStudioRoutes,
  useProStudioViewerRole,
} from '../hooks';
import { WithLoadingState } from '../router';
import { withStageNavRedirect } from './redirect-into-stage-nav';
import {
  Channel,
  formatDate,
  getChannelsFromPypes,
  getVaapiChannelLabel,
} from '../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { sendUserMessage } from '../xstate/app.xstate-utils';
import {
  channelSettingsPath,
  copyAppId,
  copyEmbedCode,
  previewChannel,
} from './channel-actions';

type ChannelsPageProps = StudioPageProps;

// {
//   "PS_BOT_MANAGER_FRONTEND_BOT_MANAGER_API_URL": "https://api.main.pypestream.dev/botmanager",
//   "PS_BOT_MANAGER_FRONTEND_S3_BUCKET_URL": "https://s3.us-east-1.amazonaws.com/duploservices-main-shared-app-bot-533267235259",
//   "PS_BOT_MANAGER_FRONTEND_OKTA_URL": "https://auth-preview.pypestream.com",
//   "PS_BOT_MANAGER_FRONTEND_OKTA_ISSUER": "https://auth-preview.pypestream.com/oauth2/auso0ahxmiTAfYxiV0h7",
//   "PS_UI_GATEWAY_REQUEST_CONTEXT": "/home/oidc_client/0oa29tr5mlvFm06lX0h8/aln5z7uhkbM6y7bMy0g7",
//   "PS_BOT_MANAGER_FRONTEND_OKTA_CLIENT_ID": "0oa29tr5mlvFm06lX0h8",
//   "PS_BOT_MANAGER_FRONTEND_HOMEPAGE": "/design-studio",
//   "PYPESTREAM_API_URL": "https://mocker-service.main.pypestream.dev",
//   "PS_AMPLITUDE_API_KEY": "9ec4c52e4f3015aed33564739e08bf6b",
//   "LAUNCHDARKLY_CLIENT_ID": "635155009f8c331296d4e63e",
//   "MIXPANEL_PROJECT_ID": "36e87b243c883b33bbb1adaf43691418",
//   "PS_ENV_CONFIGS": {
//       "sandbox": {
//           "rest": "https://webservice-sandbox.main.pypestream.dev/v3/",
//           "restApi": "https://webserviceext-sandbox.main.pypestream.dev/v3/",
//           "wcwLauncherUrl": "https://webchat-sandbox.main.pypestream.dev",
//           "wcwEnv": "sandbox",
//           "wcwDomain": "prod",
//           "HOTJAR_ID": 2852948,
//           "HOTJAR_LOCAL_ID": 2852948,
//           "styles": {
//               "fill": "#fd7e14",
//               "border": "#fd7e14",
//               "text": "#fff",
//               "label": "Sandbox"
//           }
//       },
//       "live": {
//           "rest": "https://webservice.main.pypestream.dev/v3/",
//           "restApi": "https://webserviceext.main.pypestream.devt/v3/",
//           "wcwLauncherUrl": "https://webchat.main.pypestream.dev",
//           "wcwEnv": "live",
//           "wcwDomain": "prod",
//           "HOTJAR_ID": 2852948,
//           "HOTJAR_LOCAL_ID": 2852948,
//           "styles": {
//               "fill": "#6610f2",
//               "border": "#6610f2",
//               "text": "#fff",
//               "label": "Live"
//           }
//       }
//   }
// }

// const environment = pype.env;
// const pypesLaunchUrl = window.config.PS_ENV_CONFIGS[
//   environment.toLowerCase()
// ].wcwLauncherUrl.replace("webchat", "web");

const ChannelsPage: FC<ChannelsPageProps> = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const routes = useProStudioRoutes();
  const isViewer = useProStudioViewerRole();
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);

  const { botPypes, botId, botType, projectId } = useProStudioCtxSelector(
    (ctx) => ({
      projectId: ctx.project?.id,
      botId:
        ctx.selectedBot?.uniqueId ||
        (ctx.solutions || []).find(
          ({ botSolutionId }) => ctx.selectedBot?.id === botSolutionId
        )?.id,
      botType: ctx.selectedBot?.botType || 'main',
      botPypes: ctx.botPypes || [],
    })
  );

  const channels = useMemo(() => getChannelsFromPypes(botPypes), [botPypes]);
  const analytics = useChannelAnalyticsToggle(channels);
  const { hasAnalytics } = analytics;

  const { deleteChannelModal, openModal: openDeleteChannelModal } =
    useDeleteChannelModal({
      channel: selectedChannel,
    });

  useEffect(() => {
    if (botType === 'survey') {
      navigate(routes.versions.path);
    }
  }, [botType, navigate, routes.versions.path]);

  const columns: ColumnDef<Channel>[] = useMemo(
    () => [
      {
        accessorKey: 'type',
        accessorFn: (row) => row.type,
        header: () => 'Style',
        cell: ({ row }) => {
          if (row?.original?.type === 'vaapi') {
            return html`<div
              style="width:60px;height:60px;border-radius:4px;background:#151A25;display:flex;align-items:center;justify-content:center;color:#fff;"
            >
              <ps-icon name="chat" size="medium"></ps-icon>
            </div>`;
          }
          if (row?.original?.type === 'twilio_voice') {
            return html`
              <svg
                width="60"
                height="60"
                viewBox="0 0 60 60"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="60" height="60" rx="4" fill="#151A25" />
                <mask
                  id="voice-channel-svg-path-2-inside-1_7029_64817"
                  fill="white"
                >
                  <path
                    d="M0 12H60V56C60 58.2091 58.2091 60 56 60H4C1.79086 60 0 58.2091 0 56V12Z"
                  />
                </mask>
                <path
                  d="M0 12H60V56C60 58.2091 58.2091 60 56 60H4C1.79086 60 0 58.2091 0 56V12Z"
                  fill="white"
                />
                <path
                  d="M0 12V11H-1V12H0ZM60 12H61V11H60V12ZM0 12V13H60V12V11H0V12ZM60 12H59V56H60H61V12H60ZM56 60V59H4V60V61H56V60ZM0 56H1V12H0H-1V56H0ZM4 60V59C2.34315 59 1 57.6569 1 56H0H-1C-1 58.7614 1.23858 61 4 61V60ZM60 56H59C59 57.6569 57.6569 59 56 59V60V61C58.7614 61 61 58.7614 61 56H60Z"
                  fill="#151A25"
                  mask="url(#voice-channel-svg-path-2-inside-1_7029_64817)"
                />
                <path
                  d="M32.2169 29.5687C33.2551 29.7704 34.2093 30.2759 34.9572 31.0205C35.7052 31.7652 36.213 32.715 36.4155 33.7486M32.2169 25.3359C34.3739 25.5745 36.3853 26.5361 37.9209 28.0629C39.4565 29.5897 40.425 31.591 40.6673 33.738M28.1532 37.8894C26.876 36.6179 25.8675 35.1802 25.1277 33.6462C25.064 33.5143 25.0322 33.4483 25.0078 33.3648C24.9209 33.0681 24.9833 32.7039 25.164 32.4526C25.2149 32.3819 25.2756 32.3214 25.3971 32.2005C25.7687 31.8305 25.9546 31.6455 26.076 31.4595C26.5342 30.758 26.5342 29.8537 26.076 29.1523C25.9546 28.9663 25.7687 28.7813 25.3971 28.4113L25.19 28.2051C24.6251 27.6427 24.3427 27.3616 24.0393 27.2088C23.436 26.905 22.7236 26.905 22.1203 27.2088C21.8169 27.3616 21.5345 27.6427 20.9696 28.2051L20.802 28.3719C20.2391 28.9324 19.9576 29.2126 19.7426 29.5936C19.5041 30.0163 19.3325 30.6729 19.334 31.1578C19.3353 31.5948 19.4204 31.8935 19.5907 32.4908C20.5059 35.7007 22.2326 38.7296 24.7709 41.2566C27.3092 43.7836 30.3518 45.5026 33.5761 46.4137C34.1761 46.5832 34.4761 46.668 34.915 46.6693C35.4021 46.6707 36.0616 46.4999 36.4863 46.2625C36.869 46.0484 37.1505 45.7682 37.7134 45.2078L37.881 45.041C38.4459 44.4786 38.7283 44.1974 38.8818 43.8954C39.1869 43.2948 39.1869 42.5856 38.8818 41.985C38.7283 41.683 38.4459 41.4018 37.881 40.8394L37.6738 40.6332C37.3022 40.2632 37.1164 40.0783 36.9296 39.9573C36.225 39.5013 35.3166 39.5013 34.612 39.9573C34.4251 40.0783 34.2393 40.2632 33.8677 40.6332C33.7462 40.7542 33.6855 40.8146 33.6144 40.8653C33.3621 41.0452 32.9961 41.1073 32.6981 41.0208C32.6143 40.9965 32.548 40.9648 32.4155 40.9015C30.8746 40.165 29.4304 39.1609 28.1532 37.8894Z"
                  stroke="#151A25"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            `;
          }
          const style = row?.original?.style;

          return html`
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="60"
              height="60"
              viewBox="0 0 60 60"
              fill="none"
            >
              <rect
                width="60"
                height="60"
                rx="4"
                fill=${style?.widgetStyleColor || '#8573E7'}
              />
              <mask id="path-2-inside-1_2701_6467" fill="white">
                <path
                  d="M0 12H60V56C60 58.2091 58.2091 60 56 60H4C1.79086 60 0 58.2091 0 56V12Z"
                />
              </mask>
              <path
                d="M0 12H60V56C60 58.2091 58.2091 60 56 60H4C1.79086 60 0 58.2091 0 56V12Z"
                fill="white"
              />
              <path
                d="M0 12V11H-1V12H0ZM60 12H61V11H60V12ZM0 13H60V11H0V13ZM59 12V56H61V12H59ZM56 59H4V61H56V59ZM1 56V12H-1V56H1ZM4 59C2.34315 59 1 57.6569 1 56H-1C-1 58.7614 1.23858 61 4 61V59ZM59 56C59 57.6569 57.6569 59 56 59V61C58.7614 61 61 58.7614 61 56H59Z"
                fill="#8573E7"
                mask="url(#path-2-inside-1_2701_6467)"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M8 16C6.89543 16 6 16.8954 6 18V32C6 33.5 6 34 4 35C6.15384 35 11.0087 32.6207 11 32H37C38.1046 32 39 31.1046 39 30V18C39 16.8954 38.1046 16 37 16H8Z"
                fill=${style?.agentBubbleColor || '#FFEDB4'}
              />
              <path
                d="M10 27L12.552 20.736H13.712L16.248 27H15.224L14.656 25.528H11.584L11.008 27H10ZM11.936 24.656H14.312L13.12 21.68L11.936 24.656ZM18.6506 27.032C17.4186 27.032 16.5706 26 16.5706 24.848C16.5706 23.808 17.3386 22.656 18.6826 22.656C19.0506 22.656 19.7306 22.84 20.0746 23.376V22.728H21.0026V26.944C21.0026 28.296 20.1066 29.136 18.7706 29.136C17.6266 29.136 16.9706 28.608 16.6186 27.84L17.4106 27.384C17.6266 27.816 17.9786 28.288 18.7546 28.288C19.4586 28.288 20.0506 27.88 20.0506 26.984V26.312C19.7066 26.864 18.9946 27.032 18.6506 27.032ZM17.5226 24.832C17.5226 25.568 18.0266 26.176 18.7946 26.176C19.5626 26.176 20.0666 25.568 20.0666 24.832C20.0666 24.096 19.5626 23.504 18.7946 23.504C18.0186 23.504 17.5226 24.096 17.5226 24.832ZM22.9686 25.144C22.9846 25.8 23.5926 26.256 24.2166 26.256C24.7366 26.256 25.1206 26.064 25.3686 25.592L26.0566 26.048C25.6886 26.728 24.9766 27.08 24.2326 27.08C22.9686 27.08 21.9926 26.128 21.9926 24.856C21.9926 23.648 22.9046 22.64 24.1446 22.64C25.4726 22.64 26.2246 23.56 26.2246 24.848C26.2246 24.888 26.2246 25.04 26.2086 25.144H22.9686ZM23.0166 24.424H25.2566C25.2246 23.816 24.7366 23.456 24.1446 23.456C23.5606 23.456 23.1046 23.84 23.0166 24.424ZM30.0825 24.608C30.0825 24 29.7945 23.544 29.1385 23.544C28.5385 23.544 28.1545 23.976 28.1545 24.624V27H27.2025V22.728H28.1305V23.304C28.5385 22.752 29.0985 22.64 29.4345 22.64C30.5225 22.64 31.0345 23.4 31.0345 24.368V27H30.0825V24.608ZM33.545 25.472C33.545 26 33.809 26.184 34.281 26.184C34.369 26.184 34.465 26.176 34.585 26.152V26.96C34.417 27.024 34.193 27.048 34.073 27.048C33.153 27.048 32.593 26.568 32.593 25.624V23.584H31.809V22.728H32.593V21.408H33.545V22.728H34.593V23.584H33.545V25.472Z"
                fill=${style?.agentBubbleTextColor || 'black'}
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M52 37C53.1046 37 54 37.8954 54 39V53C54 54.5 54 55 56 56C53.8462 56 48.9913 53.6207 49 53H29C27.8954 53 27 52.1046 27 51V39C27 37.8954 27.8954 37 29 37H52Z"
                fill=${style?.userBubbleColor || '#736D9D'}
              />
              <path
                d="M36.6 45.568C36.6 47.04 35.784 48.08 34.152 48.08C32.496 48.08 31.728 47 31.728 45.616V41.736H32.712V45.576C32.712 46.56 33.32 47.152 34.168 47.152C35.096 47.152 35.616 46.504 35.616 45.608V41.736H36.6V45.568ZM39.3521 48.072C38.3841 48.072 37.8721 47.624 37.5841 47L38.3281 46.584C38.5281 46.984 38.8241 47.272 39.3521 47.272C39.6961 47.272 40.0241 47.096 40.0241 46.8C40.0241 46.544 39.7441 46.368 39.1441 46.24C38.7201 46.152 38.3281 45.984 38.0801 45.728C37.8961 45.536 37.7761 45.288 37.7761 44.952C37.7761 44.152 38.5361 43.64 39.3281 43.64C40.2401 43.64 40.6801 44.072 40.9281 44.6L40.1921 45.016C40.0401 44.712 39.7841 44.44 39.2961 44.44C39.0161 44.44 38.6961 44.608 38.6961 44.856C38.6961 45.144 38.9441 45.288 39.4881 45.4C39.9281 45.496 40.3121 45.648 40.5921 45.904C40.8001 46.096 40.9681 46.384 40.9681 46.744C40.9681 47.52 40.3121 48.072 39.3521 48.072ZM42.6718 46.144C42.6878 46.8 43.2958 47.256 43.9198 47.256C44.4398 47.256 44.8238 47.064 45.0718 46.592L45.7598 47.048C45.3918 47.728 44.6798 48.08 43.9358 48.08C42.6718 48.08 41.6958 47.128 41.6958 45.856C41.6958 44.648 42.6078 43.64 43.8478 43.64C45.1758 43.64 45.9278 44.56 45.9278 45.848C45.9278 45.888 45.9278 46.04 45.9118 46.144H42.6718ZM42.7198 45.424H44.9598C44.9278 44.816 44.4398 44.456 43.8478 44.456C43.2638 44.456 42.8078 44.84 42.7198 45.424ZM49.2416 44.688C49.1616 44.672 49.0176 44.664 48.9696 44.664C48.2416 44.664 47.8576 45.096 47.8576 45.832V48H46.9056V43.728H47.8336V44.408C48.0976 43.928 48.5456 43.688 49.0096 43.688C49.0656 43.688 49.1856 43.696 49.2416 43.712V44.688Z"
                fill=${style?.userBubbleTextColor || 'white'}
              />
            </svg>
          `;
        },
      },
      {
        accessorKey: 'name',
        accessorFn: (row) => row.name,
        header: () =>
          t('pro-studio/channels:table.column1', {
            defaultValue: 'Name',
          }),
        //  instead of svg in ps-stack for voice channel
        cell: ({ row }) =>
          html`<div>
            <ps-text-subtitle size="small">
              ${row?.original?.name}
            </ps-text-subtitle>
            <ps-stack gutter="2xsmall" alignItems="center" nowrap>
              ${row?.original?.type === 'vaapi'
                ? html`<ps-icon name="chat" size="xsmall"></ps-icon>
                    <ps-text-caption
                      >${getVaapiChannelLabel(
                        row.original.vaapiChannelType
                      )}</ps-text-caption
                    >`
                : nothing}
              ${row?.original?.type === 'twilio_voice'
                ? html`<ps-icon name="call" size="xsmall"></ps-icon>
                    <ps-text-caption>Voice</ps-text-caption>`
                : nothing}
              ${row?.original?.type !== 'vaapi' &&
              row?.original?.type !== 'twilio_voice'
                ? html` <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8.00885 2.25001C5.81627 2.24637 3.78222 3.39237 2.64923 5.26967H8.1264C9.00158 5.27507 9.83842 5.62951 10.4513 6.25434C11.0591 6.86151 11.4012 7.68511 11.4023 8.54428V8.60534C11.3806 10.4155 9.91061 11.8734 8.10044 11.88H8.07449C6.26146 11.8616 4.79975 10.3896 4.794 8.57634V8.15652H6.27625V8.57634C6.28209 9.57977 7.09403 10.3918 8.09739 10.3976C9.10318 10.4086 9.92742 9.60204 9.93838 8.59618C9.94934 7.59033 9.14287 6.76604 8.13708 6.75508H1.99893C1.24828 9.33704 2.23306 12.1098 4.44413 13.6397C6.65519 15.1696 9.59691 15.1138 11.7483 13.5011C13.8998 11.8883 14.7786 9.08024 13.9306 6.52862C13.0825 3.97699 10.6976 2.25381 8.00885 2.25001Z"
                        fill="#151A25"
                      />
                      <path
                        d="M8.09846 10.4012C7.09267 10.4012 6.27732 9.58578 6.27732 8.57993V8.1601H4.79506V8.57993C4.80082 10.3932 6.26253 11.8652 8.07556 11.8835H8.10151C9.91522 11.8786 11.3881 10.4166 11.4064 8.60282V8.54176C11.4053 7.68259 11.0633 6.85899 10.4554 6.25182C9.84205 5.62568 9.00393 5.27062 8.12746 5.26562H2.6503C2.3696 5.73023 2.15097 6.22962 2 6.75103H8.13815C9.1328 6.76941 9.9273 7.58507 9.9196 8.57993C9.9196 9.58578 9.10425 10.4012 8.09846 10.4012Z"
                        fill="white"
                      />
                    </svg>
                    <ps-text-caption>Pype</ps-text-caption>`
                : nothing}
            </ps-stack>
          </div>`,
      },
      {
        accessorKey: 'env',
        accessorFn: (row) => row.env,
        header: () =>
          t('pro-studio/channels:table.column2', {
            defaultValue: 'Environment',
          }),
        cell: ({ row }) => {
          if (!row?.original?.env) {
            return null;
          }

          return html`<ps-text-subtitle size="small">
            ${t(
              `pro-studio/common:environments.${row?.original?.env.toLowerCase()}`,
              {
                defaultValue: row?.original?.env,
              }
            )}
          </ps-text-subtitle>`;
        },
      },
      {
        accessorKey: 'lastEdited',
        accessorFn: (row) => row.lastEdited,
        header: () =>
          t('pro-studio/channels:table.column3', {
            defaultValue: 'Last Edited',
          }),
        cell: ({ row }) => html`
          <ps-text-body
            >${row?.original?.lastEdited
              ? formatDate(row?.original?.lastEdited)
              : ''}</ps-text-body
          >
        `,
      },
      ...(hasAnalytics
        ? [
            {
              id: 'analytics',
              accessorFn: (row: Channel) => row.analyticsEnabled,
              header: () =>
                t('pro-studio/channels:table.column4', {
                  defaultValue: 'Analytics',
                }),
              cell: ({ row }) => {
                const isConnected = analytics.isConnected(row.original);
                const isInteractive = analytics.isInteractive(row.original);

                const handleClick = (e: Event) => {
                  e.stopPropagation();
                  analytics.toggle(row.original);
                };

                return isConnected
                  ? html`
                      <ps-tag
                        variant="success"
                        size="small"
                        ?interactive=${isInteractive}
                        @click=${handleClick}
                      >
                        <ps-stack gutter="2xsmall" alignItems="center" nowrap>
                          <ps-icon name="link" size="xsmall"></ps-icon>
                          <ps-text-caption>
                            ${t('pro-studio/channels:analytics.connected', {
                              defaultValue: 'Connected',
                            })}
                          </ps-text-caption>
                          <!-- <ps-icon name="external-link" size="xsmall"></ps-icon> -->
                          <ps-icon name="close" size="xsmall"></ps-icon>
                        </ps-stack>
                      </ps-tag>
                    `
                  : html`
                      <ps-tag
                        variant="error"
                        size="small"
                        ?interactive=${isInteractive}
                        @click=${handleClick}
                      >
                        <ps-stack gutter="2xsmall" alignItems="center" nowrap>
                          <ps-icon name="unlink" size="xsmall"></ps-icon>
                          <ps-text-caption>
                            ${t('pro-studio/channels:analytics.notConnected', {
                              defaultValue: 'Not Connected',
                            })}
                          </ps-text-caption>
                          <ps-icon name="refresh" size="xsmall"></ps-icon>
                        </ps-stack>
                      </ps-tag>
                    `;
              },
            },
          ]
        : []),
      {
        id: 'actions',
        header: () =>
          html`<ps-text-body style="opacity: 0">Actions</ps-text-body>`,
        accessorFn: (row) => row,
        cell: ({ row }) => {
          // Contact Center (vaapi) channels are managed in Contact Center: no
          // preview/embed (no widget) and no delete from here.
          if (row?.original?.type === 'vaapi') return html``;
          return row?.original?.type === 'twilio_voice'
            ? !isViewer
              ? html`<ps-stack
                  alignItems="center"
                  justifyContent="end"
                  gutter="xsmall"
                  nowrap
                  ><ps-tooltip content="Delete Channel"
                    ><ps-icon-button
                      name="delete"
                      size="small"
                      @click=${async (e: Event) => {
                        e.stopPropagation();
                        openDeleteChannelModal();
                        setSelectedChannel(row?.original);
                      }}
                    ></ps-icon-button></ps-tooltip
                ></ps-stack>`
              : null
            : html`<ps-stack
                alignItems="center"
                justifyContent="end"
                gutter="xsmall"
                nowrap
              >
                <ps-tooltip content="Preview in a new tab">
                  <ps-icon-button
                    name="show"
                    size="small"
                    @click=${async (e: Event) => {
                      e.stopPropagation();
                      previewChannel(row.original);
                    }}
                  ></ps-icon-button>
                </ps-tooltip>
                <ps-tooltip content="Copy App ID">
                  <ps-icon-button
                    name="copy"
                    size="small"
                    @click=${async (e: Event) => {
                      e.stopPropagation();
                      copyAppId(row.original, t);
                    }}
                  ></ps-icon-button>
                </ps-tooltip>
                <ps-tooltip content="Copy Embed Code">
                  <ps-icon-button
                    name="tag"
                    size="small"
                    @click=${async (e: Event) => {
                      e.stopPropagation();
                      copyEmbedCode(row.original, t);
                    }}
                  ></ps-icon-button>
                </ps-tooltip>
                ${!isViewer
                  ? html`
                      <ps-tooltip content="Delete Channel">
                        <ps-icon-button
                          name="delete"
                          size="small"
                          @click=${async (e: Event) => {
                            e.stopPropagation();
                            openDeleteChannelModal();
                            setSelectedChannel(row?.original);
                          }}
                        ></ps-icon-button>
                      </ps-tooltip>
                    `
                  : null}
              </ps-stack>`;
        },
        meta: {
          align: 'end',
        },
      },
    ],
    [analytics, hasAnalytics, isViewer, openDeleteChannelModal, t]
  );

  if (botType === 'survey') {
    return null;
  }

  return (
    <>
      <Stack gutter="large" alignItems="center" justifyContent="space-between">
        <Stack gutter="xsmall" alignItems="center">
          <TextTitle test-id="ps-channels-page-title" size="small">
            <TranslationComponent i18nKey="pro-studio/channels:title">
              Channels
            </TranslationComponent>
          </TextTitle>
          <Tooltip test-id="ps-channels-page-info-tooltip" placement="right">
            <Icon
              test-id="ps-channels-page-info-icon"
              name="info"
              color="gray"
              size="small"
            />
            <TextCaption variant="primary" slot="content">
              <TranslationComponent i18nKey="pro-studio/channels:subtitle">
                Channels are how your users will interact with your solution.
                For example, you can place a Pype on your website or in the
                future publish your solution to a 3rd party messaging channel
                (e.g. Facebook).
              </TranslationComponent>
            </TextCaption>
          </Tooltip>
        </Stack>

        {!isViewer && (
          <>
            <Button
              test-id="ps-channels-page-add-channel-button"
              size="large"
              id="ps-channels-page-add-channel-button"
            >
              <Icon name="add" slot="prefix" />{' '}
              <TranslationComponent i18nKey="pro-studio/channels:add_channel">
                Add Channel
              </TranslationComponent>
              <Icon name="chevron-down" slot="suffix" />
            </Button>

            <Dropdown
              test-id="ps-channels-page-add-channel-button-dropdown"
              trigger="ps-channels-page-add-channel-button"
            >
              <Menu>
                <MenuItem
                  onClick={() => navigate(routes.channel_creation.path)}
                >
                  <Icon name="chat" size="small" slot="prefix" />
                  Pype Channel
                </MenuItem>
                <MenuItem
                  onClick={() => navigate(routes.voice_channel_creation.path)}
                >
                  <Icon name="call" size="small" slot="prefix" />
                  Voice Channel
                </MenuItem>
              </Menu>
            </Dropdown>
          </>
        )}
      </Stack>
      <Spacer size="large" />
      <PageSection size="small">
        <DataTable<Channel>
          test-id="ps-channels-page-channels-table"
          size="small"
          columns={columns}
          data={channels}
          enable-row-selection
          onRowSelectionChange={(e: CustomEvent) => {
            const { selected } = e.detail;

            if (!selected?.length) return;

            const channel = selected[0]?.original;
            const channelId = channel?.id;

            // No Pro Studio settings page for Contact Center (vaapi) channels.
            if (channel?.type === 'vaapi') return;

            if (!channelId) {
              sendUserMessage({
                type: 'error',
                text: 'Invalid Channel (id is empty)',
                autoClose: 3000,
              });

              return;
            }

            navigate(channelSettingsPath(projectId, botId, channel));
          }}
        />
      </PageSection>
      {deleteChannelModal}
    </>
  );
};

export const Channels = withStageNavRedirect(WithLoadingState(ChannelsPage), {
  stage: 'design',
});
